import * as React from 'react';
import { useSelector } from 'react-redux';
import { lookupsLoadingFinishedSelector } from 'store/lookups/selector';
import useDebouncedState from 'hooks/useDebouncedState';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { PrimaryButton } from '@fluentui/react';
import { FormDatePicker } from 'formComponents/FormDatePicker';
import moment from 'moment-timezone';
import { formStyles } from 'styles/formStyles';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { ArtikelDetailSelect } from 'components/Lager/ArtikelDetail/ArtikelDetailSelect';
import { authSelector } from 'store/auth/selector';
import { Artikelumsatz } from './Artikelumsatz';
import { generateReportPrintUrl } from '../utils';

const styles = mergeStyleSets({
    container: {
        margin: 0,
        marginTop: 5,
        padding: 0,
        display: 'grid',
        gridTemplateRows: 'max-content 1fr',
        gridRowGap: 5,
        height: 'calc(100vh - 75px)',
    },
    searchLine: {
        display: 'grid',
        gridTemplateColumns: '160px 160px 1fr 1fr max-content max-content',
        gridColumnGap: 10,
        marginTop: 10,
    },
    actionButtons: {
        marginTop: 29,
        display: 'grid',
        gridTemplateColumns: 'repeat(2, max-content)',
        gridColumnGap: 10,
    },
});

interface FilterValues {
    datumVon: string;
    datumBis: string;
    artikelGruppeNr: number;
    artikelNr: string;
    showCompare: boolean;
}

const defaultFilterValues: FilterValues = {
    datumVon: moment().startOf('month').format('YYYY-MM-DD'), // '2021-10-01'
    datumBis: moment().endOf('month').format('YYYY-MM-DD'), // '2021-10-31'
    artikelGruppeNr: 0,
    artikelNr: '',
    showCompare: true,
};

export const ArtikelumsatzView: React.FC = () => {
    const auth = useSelector(authSelector);
    const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);
    const [filterValues, debouncedFilterValues, setFilterValues] = useDebouncedState<FilterValues>({
        ...defaultFilterValues,
    });

    if (!lookupsLoadingFinished) {
        return <div />;
    }

    return (
        <div className={styles.container}>
            <div className={styles.searchLine}>
                <FormDatePicker
                    value={filterValues.datumVon}
                    label="Datum Von"
                    options={{
                        required: true,
                    }}
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            datumVon: moment(newValue).format('YYYY-MM-DD') + 'T00:00:00',
                        });
                    }}
                />

                <FormDatePicker
                    value={filterValues.datumBis}
                    label="Datum Bis"
                    options={{
                        required: true,
                    }}
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            datumBis: moment(newValue).format('YYYY-MM-DD') + 'T00:00:00',
                        });
                    }}
                />

                <div>
                    <ArtikelDetailSelect
                        artikelGruppeNr={filterValues.artikelGruppeNr}
                        artikelNr={filterValues.artikelNr}
                        onChange={async (newArtikelDetail) => {
                            setFilterValues({
                                ...filterValues,
                                artikelGruppeNr: newArtikelDetail?.artikelGruppeNr || 0,
                                artikelNr: newArtikelDetail?.artikelNr || '',
                            });
                        }}
                    />
                </div>

                <div />

                <Checkbox
                    className={formStyles.checkboxInsideFormAligned}
                    label="Vergleich VJ/3J"
                    checked={filterValues.showCompare}
                    onChange={(event, checked) => {
                        setFilterValues({
                            ...filterValues,
                            showCompare: !!checked,
                        });
                    }}
                />

                <div className={styles.actionButtons}>
                    {auth.rolle !== 'AD' && auth.rolle !== 'EX' && (
                        <PrimaryButton
                            onClick={() => {
                                window.open(
                                    generateReportPrintUrl('artikelumsatz', {
                                        datumVon: filterValues.datumVon,
                                        datumBis: filterValues.datumBis,
                                        artikelGruppeNr: filterValues.artikelGruppeNr,
                                        artikelNr: filterValues.artikelNr,
                                        showCompare: filterValues.showCompare,
                                    }),
                                );
                            }}
                            text="Drucken"
                        />
                    )}
                </div>
            </div>

            <Artikelumsatz
                datumVon={debouncedFilterValues.datumVon}
                datumBis={debouncedFilterValues.datumBis}
                artikelGruppeNr={debouncedFilterValues.artikelGruppeNr}
                artikelNr={debouncedFilterValues.artikelNr}
                showCompare={debouncedFilterValues.showCompare}
            />
        </div>
    );
};
